import { useCallback, useEffect, useState } from "react";

import { MedplumClient, resolveId } from "@medplum/core";
import { Bundle, Condition, Observation, Patient, Reference } from "@medplum/fhirtypes";
import { useMedplum, useSubscription } from "@medplum/react";

import { Extensions } from "@/fhir/enums/extensions";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { PastPregnanciesCodes } from "@/fhir/enums/quilted-health";
import { fetchPregnancyRecords, PregnancyRecord } from "@/pregnancy";
import { AppDispatch } from "@/store";
import { upsertProblems } from "@/store/slices/client-problems";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";

import getPregnancyData, { PregnancyDataType } from "./getPregnancyData";

export interface PregnancyObservations {
  /**
   * Current active PregnancyRecord from app/pregnancy. Source of truth for pregnancy
   * status, EDD, LMP, and current gestational age.
   */
  activePregnancyRecord?: PregnancyRecord;
  previousPregnancyObservations?: Bundle<Observation>;
  parityObservation?: Observation;
  totalNumberOfPregnanciesObservation?: Observation;
  numberOfTermPregnanciesObservation?: Observation;
  numberOfPrematureBirthsObservation?: Observation;
  numberOfAbortionsObservation?: Observation;
  numberOfMiscarriagesObservation?: Observation;
  numberOfLivingChildrenObservation?: Observation;
  numberOfTotalAbortionsObservation?: Observation;
}

export async function fetchAllObservations({
  medplum,
  subject,
}: {
  medplum: MedplumClient;
  subject: Reference<Patient>;
  sort?: string;
}): Promise<PregnancyObservations> {
  const subjectReference = subject.reference ?? "";
  const patientId = resolveId(subject);

  if (!patientId) {
    return {
      activePregnancyRecord: undefined,
      previousPregnancyObservations: undefined,
      parityObservation: undefined,
      totalNumberOfPregnanciesObservation: undefined,
      numberOfTermPregnanciesObservation: undefined,
      numberOfPrematureBirthsObservation: undefined,
      numberOfAbortionsObservation: undefined,
      numberOfMiscarriagesObservation: undefined,
      numberOfLivingChildrenObservation: undefined,
      numberOfTotalAbortionsObservation: undefined,
    };
  }

  const [pregnancyRecords, previousPregnancyObservations] = await Promise.all([
    fetchPregnancyRecords(medplum, patientId, { status: "current" }),
    // GTPAL past-pregnancy observations are standard FHIR and not managed by app/pregnancy.
    medplum.search("Observation", {
      subject: subjectReference,
      code: [
        PastPregnanciesCodes.NPregnancies,
        PastPregnanciesCodes.NTotalPregnancies,
        PastPregnanciesCodes.NTermPregnancies,
        PastPregnanciesCodes.NPrematureBirths,
        PastPregnanciesCodes.NInducedAbortions,
        PastPregnanciesCodes.NMiscarriages,
        PastPregnanciesCodes.NLivingChildren,
        PastPregnanciesCodes.NTotalAbortions,
        LOINCCodes.NPregnancies,
      ].join(","),
      _sort: "-_lastUpdated",
      _count: 1000,
    }),
  ]);

  const activePregnancyRecord = pregnancyRecords[0];

  const getSingleObservationByCode = (bundle: Bundle<Observation> | undefined) => {
    return (code: string) => {
      return bundle?.entry?.find((entry) =>
        entry.resource?.code.coding?.some((c) => c.code === code),
      )?.resource;
    };
  };

  const findPreviousPregnancyObservation = getSingleObservationByCode(
    previousPregnancyObservations,
  );

  return {
    activePregnancyRecord,
    previousPregnancyObservations,
    totalNumberOfPregnanciesObservation: findPreviousPregnancyObservation(LOINCCodes.NPregnancies),
    parityObservation: findPreviousPregnancyObservation(LOINCCodes.NPregnancies),
    numberOfTermPregnanciesObservation: findPreviousPregnancyObservation(
      PastPregnanciesCodes.NTermPregnancies,
    ),
    numberOfPrematureBirthsObservation: findPreviousPregnancyObservation(
      PastPregnanciesCodes.NPrematureBirths,
    ),
    numberOfAbortionsObservation: findPreviousPregnancyObservation(
      PastPregnanciesCodes.NInducedAbortions,
    ),
    numberOfMiscarriagesObservation: findPreviousPregnancyObservation(
      PastPregnanciesCodes.NMiscarriages,
    ),
    numberOfLivingChildrenObservation: findPreviousPregnancyObservation(
      PastPregnanciesCodes.NLivingChildren,
    ),
    numberOfTotalAbortionsObservation: findPreviousPregnancyObservation(
      PastPregnanciesCodes.NTotalAbortions,
    ),
  };
}

export function usePregnancyObservations({ clientId }: { clientId: string }) {
  const medplum = useMedplum();
  const [pregnancyData, setPregnancyData] = useState<PregnancyDataType | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshData, setRefresh] = useState(false);
  const refresh = () => setRefresh(!refreshData);

  useEffect(() => {
    async function fetchData() {
      const subject = { reference: joinReferenceString("Patient", clientId) };
      const fetchedObservations = await fetchAllObservations({ medplum, subject });
      const data = await getPregnancyData(fetchedObservations);
      setPregnancyData(data);
    }
    // Clear previous data immediately when clientId changes
    setPregnancyData(undefined);
    setIsLoading(true);
    fetchData()
      .catch(() => {
        setPregnancyData(undefined);
      })
      .finally(() => setIsLoading(false));
  }, [refreshData, medplum, clientId]);

  return { pregnancyData, isLoading, refresh };
}

type ProblemsSubscriptionProps = {
  extensionValue: string;
  subscriptionCriteria: string;
  dispatch: AppDispatch;
};

export const useProblemsSubscription = ({
  subscriptionCriteria,
  dispatch,
  extensionValue,
}: ProblemsSubscriptionProps) => {
  const handleBundle = useCallback(
    (bundle: Bundle) => {
      const conditions = getResourcesFromBundle("Condition", bundle) as Condition[];
      if (!conditions || conditions.length === 0) return;

      dispatch(upsertProblems(conditions));
    },
    [dispatch],
  );

  useSubscription(subscriptionCriteria, handleBundle, {
    onError: (error: Error) => {
      console.error(error);
    },
    subscriptionProps: {
      extension: [
        {
          url: Extensions.SubscriptionSupportedInteraction,
          valueCode: extensionValue,
        },
      ],
    },
  });
};
